import { useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Step-by-step provider setup guides rendered inside the Platform Credentials
 * page, next to the credential fields each guide produces. Written for a
 * platform admin who may not be a developer (the Meta guide is followed by
 * whoever owns the business's Facebook assets). The OAuth redirect URI is
 * computed from the current origin so every tenant shows its own exact value.
 */

interface GuideStep {
  title: string;
  lines: string[];
}

interface GuideSection {
  heading: string;
  steps: GuideStep[];
}

function redirectUri(): string {
  return `${window.location.origin}/admin/advertising/platforms`;
}

function googleGuide(): GuideSection[] {
  const uri = redirectUri();
  return [
    {
      heading: 'A. OAuth client (Google Cloud, ~5 min) → Client ID + Client Secret',
      steps: [
        {
          title: 'Create or select a Cloud project',
          lines: [
            'Go to console.cloud.google.com and create a project (one platform-level project serves every site — name it after the platform, not the site).',
          ],
        },
        {
          title: 'Enable the Google Ads API',
          lines: ['APIs & Services → Library → search "Google Ads API" → Enable.'],
        },
        {
          title: 'Configure the OAuth consent screen',
          lines: [
            'APIs & Services → OAuth consent screen: choose External and fill the required fields.',
            'Add the Google account that will click Connect as a Test user.',
            'IMPORTANT: after setup, click "Publish app" (to In production). Apps left in Testing status expire refresh tokens every 7 days, so the ad-platform connection would die weekly. Published-but-unverified is fine for your own use — the consent screen just shows an "unverified app" warning you can click through (Advanced → Continue).',
          ],
        },
        {
          title: 'Create the OAuth client',
          lines: [
            'APIs & Services → Credentials → Create credentials → OAuth client ID → application type "Web application".',
            `Add this EXACT Authorized redirect URI: ${uri}`,
            'Copy the Client ID and Client secret into the Google Ads fields on this page.',
          ],
        },
      ],
    },
    {
      heading: 'B. Developer token (~5 min) → Developer Token',
      steps: [
        {
          title: 'Create (or use) a production manager account',
          lines: [
            'You need a regular Google Ads MANAGER account (MCC) — free, no billing: ads.google.com/home/tools/manager-accounts.',
          ],
        },
        {
          title: 'Get the token from the API Center',
          lines: [
            'Inside the manager account: Admin (gear) → API Center → complete the short access form.',
            'The developer token appears immediately at "Test account" access level. Paste it into the Developer Token field on this page.',
            'The token identifies this application — one token serves every site and every connected account, and it never changes as access levels upgrade.',
          ],
        },
      ],
    },
    {
      heading: 'C. Test accounts (recommended before any real spend)',
      steps: [
        {
          title: 'Create a test manager account',
          lines: [
            'A test-level token can ONLY call TEST Google Ads accounts — and test accounts can never serve ads or spend money.',
            'Create one via https://ads.google.com/nav/selectaccount?sf=mt — the "?sf=mt" is what routes to the test flow. If you land in a "new campaign" wizard, abort: wrong flow.',
            'Sign in with a Google account NOT linked to your production manager account (create a fresh one if needed — this is a documented Google requirement).',
          ],
        },
        {
          title: 'Create a test client account under it',
          lines: [
            'In the test manager: Accounts → ⊕ → Create new account. Every client created under a test manager is automatically a test account.',
          ],
        },
        {
          title: 'Grant the connecting user direct access',
          lines: [
            'Open the test client → Admin → Access and security → invite the Google account that will click Connect, as Admin, and accept the emailed invite.',
            'The Connect flow only lists accounts the signed-in user has DIRECT access to — and campaigns cannot be created in a manager account, so you must be able to pick the client itself.',
          ],
        },
      ],
    },
    {
      heading: 'D. Production: apply for Basic Access (~5 business days)',
      steps: [
        {
          title: 'Prerequisites',
          lines: [
            'In the PRODUCTION manager account, open Admin → API Center and check the API contact email is current — Google uses it for the application.',
            'Link every active Google Ads account you intend to manage under this manager account.',
          ],
        },
        {
          title: 'Apply',
          lines: [
            'In the API Center, apply for Basic Access and describe the tool (campaign management for this platform’s own properties).',
            'Review takes about 5 business days. Basic Access allows 15,000 operations/day across test AND production accounts.',
            'Nothing changes on this page when approved — the same token simply starts working against production accounts. Connect the production account on the Ad Platforms page once approved.',
          ],
        },
      ],
    },
  ];
}

function metaGuide(): GuideSection[] {
  const uri = redirectUri();
  return [
    {
      heading: 'Prerequisites (who should follow this guide)',
      steps: [
        {
          title: 'You need a Facebook account with admin access to all three business assets',
          lines: [
            'The Meta Business Portfolio (business.facebook.com, formerly Business Manager), the ad account you will advertise from, and the business’s Facebook Page.',
          ],
        },
      ],
    },
    {
      heading: '1. Create the app → App ID + App Secret',
      steps: [
        {
          title: 'Create a Business-type app',
          lines: [
            'Go to developers.facebook.com/apps (accept the developer terms if this account has never used it) → Create App.',
            'Pick the Business app type — in the newer use-case flow choose "Other" and then "Business". Name it after the business and select the Business Portfolio when asked.',
          ],
        },
        {
          title: 'Add the two required products',
          lines: [
            'From the app dashboard, add: "Marketing API", and "Facebook Login for Business".',
            `Under Facebook Login for Business → Settings, add this EXACT Valid OAuth Redirect URI: ${uri}`,
          ],
        },
        {
          title: 'Copy the credentials into this page',
          lines: [
            'App dashboard → App settings → Basic: copy the App ID and the App secret (click Show; you may be asked for your Facebook password) into the Meta Ads fields on this page.',
          ],
        },
      ],
    },
    {
      heading: '2. Development Mode is enough — add the connecting person',
      steps: [
        {
          title: 'No App Review needed for our use',
          lines: [
            'The app can stay in Development Mode: it works only for people with a role on the app, and only against ad accounts / Pages they control — which is exactly this use.',
            'Under App roles → Roles, add the person who will click Connect as an Administrator; they must accept the invite (check facebook.com notifications).',
            'App Review / Advanced Access + business verification are only needed if the app should ever serve people outside your business. Not required here.',
          ],
        },
      ],
    },
    {
      heading: '3. What Connect will ask for',
      steps: [
        {
          title: 'Permissions and account selection',
          lines: [
            'The Connect button on the Ad Platforms page opens Facebook login requesting: ads_management, ads_read, business_management.',
            'It then lists the ad accounts the connecting user can access — pick the right one. You can check the account’s "act_" number at adsmanager.facebook.com in the account dropdown.',
          ],
        },
      ],
    },
    {
      heading: '4. Page ID (REQUIRED before any ad can be created)',
      steps: [
        {
          title: 'Find the numeric Page ID',
          lines: [
            'Meta ads are delivered "from" a Facebook Page — ad creation fails without it.',
            'Find it on the Page → Settings (or About → Page transparency), or at business.facebook.com → Settings → Accounts → Pages. It is a long number.',
          ],
        },
        {
          title: 'Enter it on the Ad Platforms page (not this page)',
          lines: [
            'After connecting, open Admin → Advertising → Ad Platforms: the connected Meta card has a "Page ID" field — save the number there.',
          ],
        },
      ],
    },
  ];
}

const GUIDES: { id: string; title: string; produces: string; sections: () => GuideSection[] }[] = [
  {
    id: 'google_ads',
    title: 'Google Ads — full setup guide',
    produces: 'Produces: Google Ads Client ID, Client Secret, Developer Token',
    sections: googleGuide,
  },
  {
    id: 'meta_ads',
    title: 'Meta (Facebook/Instagram) Ads — full setup guide',
    produces: 'Produces: Meta Ads App ID, App Secret — plus a Page ID entered on the Ad Platforms page',
    sections: metaGuide,
  },
];

export default function AdvertisingSetupGuides() {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="mb-4 space-y-2">
      {GUIDES.map((guide) => {
        const isOpen = open === guide.id;
        return (
          <div key={guide.id} className="border border-stone-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : guide.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-stone-50 transition-colors"
            >
              {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
              <BookOpen className="w-4 h-4 flex-shrink-0 text-text-muted" />
              {guide.title}
              <span className="ml-auto text-xs font-normal text-text-muted hidden sm:block">{guide.produces}</span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 pt-1 space-y-4 text-sm text-text-secondary bg-stone-50/50">
                {guide.sections().map((section) => (
                  <div key={section.heading}>
                    <h4 className="font-semibold text-text-primary mb-1.5">{section.heading}</h4>
                    <ol className="space-y-2 list-decimal ml-5">
                      {section.steps.map((step) => (
                        <li key={step.title}>
                          <span className="font-medium text-text-primary">{step.title}.</span>
                          <ul className="mt-0.5 space-y-0.5 list-disc ml-4">
                            {step.lines.map((line, i) => (
                              <li key={i} className="break-words">{line}</li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
